import React, { useEffect, useMemo } from "react";
import { CircleMarker, Pane, Popup, Tooltip, useMap } from "react-leaflet";
import { ELECTION_MAP_STANDARD_V1 } from "./mapRenderingStandard";

export const POINT_KIND_STYLES = {
  zs: { color: "#2563eb", label: "ZŠ" },
  ms: { color: "#16a34a", label: "MŠ" },
  ds: { color: "#f97316", label: "DS" },
  other: { color: "#7c3aed", label: "Ostatní" },
};

export function getPointKindStyle(kind) {
  return POINT_KIND_STYLES[kind] || POINT_KIND_STYLES.other;
}

export function getPointLayerRadius(value, maxValue, minRadius = 7, maxRadius = 22) {
  const amount = Math.max(0, Number(value) || 0);
  const max = Math.max(0, Number(maxValue) || 0);
  if (max <= 0) return minRadius;
  const t = Math.sqrt(amount / max);
  return Math.max(minRadius, Math.min(maxRadius, minRadius + (maxRadius - minRadius) * t));
}

export function getPointMarkerPathOptions(kind) {
  const style = getPointKindStyle(kind);
  return {
    color: "#ffffff",
    weight: 2,
    opacity: 1,
    fillColor: style.color,
    fillOpacity: 0.86,
  };
}

function PointLayerBoundsFitter({ markers, enabled, padding, maxZoom }) {
  const map = useMap();
  const lastAppliedRequestRef = React.useRef("");
  const signature = useMemo(
    () => markers.map((marker) => `${marker.id}:${marker.lat}:${marker.lng}`).join("|"),
    [markers]
  );

  useEffect(() => {
    const resolvedRequestKey = typeof enabled === "string" ? enabled : "initial";
    if (!enabled || markers.length === 0 || lastAppliedRequestRef.current === resolvedRequestKey) return;
    const positions = markers
      .map((marker) => [Number(marker.lat), Number(marker.lng)])
      .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    if (!positions.length) return;

    let cancelled = false;
    const timers = [];
    const schedule = (delay, fn) => {
      const timer = setTimeout(fn, delay);
      timers.push(timer);
    };
    const fitToMarkers = () => {
      if (cancelled) return;
      try {
        const size = typeof map.getSize === "function" ? map.getSize() : null;
        if (size && (Number(size.x) <= 0 || Number(size.y) <= 0)) return;
        map.__volby_programmatic_move_depth = (map.__volby_programmatic_move_depth || 0) + 1;
        if (typeof map.invalidateSize === "function") map.invalidateSize(false);
        if (positions.length === 1 && typeof map.setView === "function") {
          map.setView(positions[0], Math.min(Number(maxZoom) || 14, 15));
        } else if (typeof map.fitBounds === "function") {
          map.fitBounds(positions, { padding, maxZoom });
        }
        lastAppliedRequestRef.current = resolvedRequestKey;
      } catch (_) {
      } finally {
        try {
          map.__volby_programmatic_move_depth = Math.max((map.__volby_programmatic_move_depth || 1) - 1, 0);
        } catch (_) {}
      }
    };

    schedule(0, fitToMarkers);
    schedule(80, fitToMarkers);
    schedule(240, fitToMarkers);

    return () => {
      cancelled = true;
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [enabled, map, markers, maxZoom, padding, signature]);

  return null;
}

export default function PointLayer({
  markers,
  paneName = "volby-point-layer",
  paneZIndex = ELECTION_MAP_STANDARD_V1.paneZIndexes.markers,
  fitBounds = true,
  fitBoundsRequestKey,
  fitBoundsPadding = [34, 34],
  fitBoundsMaxZoom = 15,
}) {
  const safeMarkers = Array.isArray(markers)
    ? markers.filter(
        (marker) =>
          marker.lat !== null &&
          marker.lat !== undefined &&
          marker.lng !== null &&
          marker.lng !== undefined &&
          Number.isFinite(Number(marker.lat)) &&
          Number.isFinite(Number(marker.lng))
      )
    : [];
  const maxValue = safeMarkers.reduce((max, marker) => Math.max(max, Number(marker.value) || 0), 0);

  return (
    <Pane name={paneName} style={{ zIndex: paneZIndex }}>
      <PointLayerBoundsFitter
        markers={safeMarkers}
        enabled={fitBounds ? fitBoundsRequestKey || "initial" : false}
        padding={fitBoundsPadding}
        maxZoom={fitBoundsMaxZoom}
      />
      {safeMarkers.map((marker) => {
        const kindStyle = getPointKindStyle(marker.kind);
        const radius = getPointLayerRadius(marker.value, maxValue);
        return (
          <CircleMarker
            key={marker.id}
            center={[Number(marker.lat), Number(marker.lng)]}
            radius={radius}
            pathOptions={getPointMarkerPathOptions(marker.kind)}
          >
            <Tooltip direction="top" offset={[0, -radius]} opacity={0.96}>
              <strong>{marker.tooltip?.title || marker.label}</strong>
              <br />
              <span>{marker.tooltip?.subtitle || marker.kindLabel || kindStyle.label}</span>
              <br />
              <span>{marker.tooltip?.valueLabel || ""}</span>
            </Tooltip>
            <Popup>
              <div style={{ minWidth: 220 }}>
                <strong>{marker.popup?.title || marker.label}</strong>
                <div>{marker.popup?.typeLabel || marker.kindLabel || kindStyle.label}</div>
                <div>{marker.popup?.address || ""}</div>
                <div>{marker.popup?.quarter || ""}</div>
                <hr />
                <div>
                  <strong>{marker.popup?.amountCompactLabel || marker.tooltip?.valueLabel || ""}</strong>
                </div>
                {marker.popup?.amountExactLabel ? <div>{marker.popup.amountExactLabel}</div> : null}
                <div>Počet investic: {marker.popup?.investmentCount ?? marker.count ?? 0}</div>
                {Array.isArray(marker.popup?.topActivities) && marker.popup.topActivities.length ? (
                  <ul>
                    {marker.popup.topActivities.slice(0, 3).map((activity, index) => (
                      <li key={`${marker.id}-activity-${index}`}>{activity}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </Popup>
          </CircleMarker>
        );
      })}
    </Pane>
  );
}
